import { cn } from "@/lib/utils";
import { ComponentProps, InputHTMLAttributes } from "react";

import { IconBox } from "../IconBox";

type IconStyle = {
    name: IconName;
    enabled: {
        color: string;
        backgroundColor?: string;
        fill?: "none" | "current";
    };
    hover: {
        name?: IconName;
        color: string;
        backgroundColor?: string;
        fill?: "none" | "current";
    };
};

type IconSpec = {
    checked: IconStyle;
    unchecked: IconStyle;
};

type ToggleIconProps = {
    checked: boolean;
    iconSpec: IconSpec;
    size?: ComponentProps<typeof IconBox>["size"];
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size">;

const GREY_TEXT_CLASSES: Record<string, string> = {
    "grey-100": "text-grey-100",
    "grey-200": "text-grey-200",
    "grey-300": "text-grey-300",
    "grey-400": "text-grey-400",
    "grey-500": "text-grey-500",
    "grey-600": "text-grey-600",
    "grey-700": "text-grey-700",
    "grey-800": "text-grey-800",
    "grey-900": "text-grey-900"
};

const GREY_BG_CLASSES: Record<string, string> = {
    "grey-100": "bg-grey-100",
    "grey-200": "bg-grey-200",
    "grey-300": "bg-grey-300",
    "grey-400": "bg-grey-400",
    "grey-500": "bg-grey-500",
    "grey-600": "bg-grey-600",
    "grey-700": "bg-grey-700",
    "grey-800": "bg-grey-800",
    "grey-900": "bg-grey-900"
};

const FIXED_BORDER_CLASS = "border border-grey-300";
const OUTLINED_FILL_CLASS = "[&_*]:fill-none [&_*]:stroke-current";

const getTextColorClass = (color: string): string => {
    if (color.startsWith("text-")) return color;

    if (color === "var(--subcolor-orange)" || color === "--subcolor-orange") {
        return "text-subcolor-orange";
    }
    if (
        color === "var(--common-white-main)" ||
        color === "--common-white-main"
    ) {
        return "text-common-white-main";
    }
    if (
        color === "var(--common-black-main)" ||
        color === "--common-black-main"
    ) {
        return "text-common-black-main";
    }

    if (color.startsWith("var(") || color.startsWith("--")) {
        return `text-[${color}]`;
    }

    return GREY_TEXT_CLASSES[color] || `text-[${color}]`;
};

const getBackgroundClass = (bgColor?: string): string => {
    if (!bgColor) return "";
    if (bgColor === "white") return "bg-common-white-main";
    if (bgColor === "transparent") return "bg-transparent";
    if (bgColor.startsWith("var(") || bgColor.startsWith("--")) {
        return `bg-[${bgColor}]`;
    }
    return GREY_BG_CLASSES[bgColor] || `bg-[${bgColor}]`;
};

export function ToggleIcon({
    className,
    checked = false,
    iconSpec,
    size = "medium",
    ...props
}: ToggleIconProps) {
    const { name, enabled, hover } = checked
        ? iconSpec.checked
        : iconSpec.unchecked;

    const hasDifferentHoverIcon = hover?.name && hover.name !== name;
    const hasDifferentHoverColor =
        hover?.color && hover.color !== enabled.color;
    const needsHoverState = hasDifferentHoverIcon || hasDifferentHoverColor;

    const enabledColorClass = getTextColorClass(enabled.color);
    const hoverColorClass = hover?.color
        ? getTextColorClass(hover.color)
        : enabledColorClass;
    const enabledBgClass = getBackgroundClass(enabled.backgroundColor);
    const hoverBgClass = hover?.backgroundColor
        ? getBackgroundClass(hover.backgroundColor)
        : "";

    const enabledFillClass = enabled.fill === "none" ? OUTLINED_FILL_CLASS : "";
    const hoverFillClass =
        hover?.fill === "none" ? OUTLINED_FILL_CLASS : enabledFillClass;

    return (
        <label
            className={cn(
                "group inline-flex rounded-md p-1",
                enabledBgClass,
                FIXED_BORDER_CLASS,
                hoverBgClass && `hover:${hoverBgClass}`,
                className
            )}
        >
            <input
                className="hidden"
                readOnly
                type="checkbox"
                checked={checked}
                onClick={(e) => e.stopPropagation()}
                {...props}
            />

            {needsHoverState ? (
                <>
                    <IconBox
                        className={cn(
                            enabledColorClass,
                            enabledFillClass,
                            "cursor-pointer group-hover:hidden"
                        )}
                        name={name}
                        size={size}
                    />
                    <IconBox
                        className={cn(
                            hoverColorClass,
                            hoverFillClass,
                            "hidden cursor-pointer group-hover:block"
                        )}
                        name={hover?.name || name}
                        size={size}
                    />
                </>
            ) : (
                <IconBox
                    className={cn(
                        enabledColorClass,
                        enabledFillClass,
                        "cursor-pointer"
                    )}
                    name={name}
                    size={size}
                />
            )}
        </label>
    );
}
